/**
 * DocsPage — lukhas.dev/docs
 *
 * Developer documentation portal. Features the Constellation CLI dashboard
 * as the hero section, with documentation cards and API reference below.
 * Uses the approved dark terminal design system.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions, Footer, Button } from '@lukhas/ui'

// ── Design tokens (approved palette) ─────────────────────────────────────────

const MONO = `'SF Mono', 'Cascadia Code', 'JetBrains Mono', 'Fira Code', monospace`
const DISPLAY = `'DM Sans', 'Helvetica Neue', -apple-system, sans-serif`

const C = {
  bg:           '#08090C',
  surface:      '#0E1015',
  surfaceHover: '#13161D',
  border:       '#1A1E28',
  borderSubtle: '#14171F',
  accent:       '#C8CCD8',
  text:         '#A0A6B4',
  textDim:      '#5A6070',
  textGhost:    '#333844',
  white:        '#E8ECF2',
  lambda:       'rgba(200, 204, 216, 0.9)',
  lambdaGlow:   'rgba(200, 204, 216, 0.04)',
} as const

// ── Static data ───────────────────────────────────────────────────────────────

const STARS = [
  { code: 'IDN', name: 'Identity',  status: 'secured',  health: 98 },
  { code: 'MEM', name: 'Memory',    status: 'syncing',   health: 95 },
  { code: 'VIS', name: 'Vision',    status: 'online',    health: 100 },
  { code: 'BIO', name: 'Bio',       status: 'online',    health: 97 },
  { code: 'DRM', name: 'Dream',     status: 'standby',   health: 88 },
  { code: 'ETH', name: 'Ethics',    status: 'watching',  health: 100 },
  { code: 'GRD', name: 'Guardian',  status: 'online',    health: 99 },
  { code: 'QNT', name: 'Quantum',   status: 'standby',   health: 92 },
]

const PIPELINE = [
  { letter: 'M', label: 'Memory' },
  { letter: 'A', label: 'Attention' },
  { letter: 'T', label: 'Thought' },
  { letter: 'R', label: 'Risk' },
  { letter: 'I', label: 'Intent' },
  { letter: 'Z', label: 'Action' },
]

const LOG_POOL = [
  { message: 'constellation health check passed',     type: 'success' as const },
  { message: 'identity token refreshed — sovereign',  type: 'success' as const },
  { message: 'ethics watchdog sweep complete',         type: 'success' as const },
  { message: 'dream engine entering standby',          type: 'info'    as const },
  { message: 'memory fold operation completed',        type: 'info'    as const },
  { message: 'guardian drift-check nominal',           type: 'success' as const },
  { message: 'quantum coherence within bounds',        type: 'info'    as const },
  { message: 'bio adaptation cycle triggered',         type: 'info'    as const },
]

const COMMANDS: Record<string, string[]> = {
  help:    ['Available: status, list modules, health, version, docs', ''],
  status:  ['● Constellation online — 8/8 stars active', '● MATRIZ pipeline nominal', '● Guardian drift score 0.023 / 0.15 threshold', ''],
  'list modules': ['IDN  identity     secured', 'MEM  memory       syncing', 'VIS  vision       online', 'BIO  bio          online', 'DRM  dream        standby', 'ETH  ethics       watching', 'GRD  guardian     online', 'QNT  quantum      standby', ''],
  health:  ['System health: 96.1%', 'Uptime: 99.99%', 'Memory folds: 482 / 1000', ''],
  version: ['LUKHAS v4.2.1 — Constellation Framework', 'Build: 2026-03-20', ''],
  docs:    ['Opening docs... you\'re already here.', ''],
  clear:   [],
}

function nowTime(): string {
  return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

function useStagger(count: number, base = 80, start = 600): boolean[] {
  const [vis, setVis] = useState<boolean[]>(Array(count).fill(false))
  useEffect(() => {
    const ts = Array.from({ length: count }, (_, i) =>
      setTimeout(() => setVis(prev => { const n = [...prev]; n[i] = true; return n }), start + i * base)
    )
    return () => ts.forEach(clearTimeout)
  }, [count, base, start])
  return vis
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LambdaMark() {
  return (
    <div style={{ position: 'relative', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: -20, borderRadius: '50%', background: `radial-gradient(circle, ${C.lambdaGlow} 0%, transparent 70%)`, animation: 'dp-breathe 4s ease-in-out infinite' }} />
      <span style={{ fontFamily: DISPLAY, fontSize: 38, fontWeight: 300, color: C.lambda, letterSpacing: '-0.02em', position: 'relative', zIndex: 1 }}>&#923;</span>
    </div>
  )
}

function MetricCell({ label, value, unit = '', visible = true, delay = 0 }: { label: string; value: string; unit?: string; visible?: boolean; delay?: number }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(8px)', transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}ms` }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
        <span style={{ fontFamily: MONO, fontSize: 20, fontWeight: 400, color: C.white, letterSpacing: '-0.02em' }}>{value}</span>
        {unit && <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim }}>{unit}</span>}
      </div>
    </div>
  )
}

function StarRowItem({ code, name, status, health, visible = true, idx = 0 }: { code: string; name: string; status: string; health: number; visible?: boolean; idx?: number }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px',
        alignItems: 'center', padding: '14px 20px', cursor: 'default',
        background: hovered ? C.surfaceHover : 'transparent',
        borderBottom: `1px solid ${C.borderSubtle}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-12px)',
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, background 0.2s ease`,
      }}
    >
      <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, color: C.accent, letterSpacing: '0.06em' }}>{code}</span>
      <span style={{ fontFamily: DISPLAY, fontSize: 14, color: hovered ? C.white : C.text, transition: 'color 0.2s' }}>{name}</span>
      <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{status}</span>
      <div style={{ height: 2, background: C.border, borderRadius: 1, overflow: 'hidden' }}>
        <div style={{ width: `${health}%`, height: '100%', background: C.accent, opacity: 0.5, borderRadius: 1 }} />
      </div>
      <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, textAlign: 'right', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s' }}>&#8594;</span>
    </div>
  )
}

function SectionLabel({ children, visible = true, delay = 0 }: { children: React.ReactNode; visible?: boolean; delay?: number }) {
  return (
    <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '0 20px', marginBottom: 2, opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${delay}ms` }}>{children}</div>
  )
}

// ── Interactive command input ──────────────────────────────────────────────────

interface TerminalLine { id: number; type: 'input' | 'output'; text: string }

function TerminalPanel({ mounted }: { mounted: boolean }) {
  const [input, setInput] = useState('')
  const [history, setHistory] = useState<TerminalLine[]>([
    { id: 0, type: 'output', text: 'LUKHAS Terminal — type `help` for commands' },
  ])
  const [nextId, setNextId] = useState(1)
  const inputRef = useRef<HTMLInputElement>(null)

  const run = useCallback((cmd: string) => {
    const trimmed = cmd.trim().toLowerCase()
    const newLines: TerminalLine[] = [
      { id: nextId, type: 'input', text: `$ ${cmd}` },
    ]
    let id = nextId + 1

    if (trimmed === 'clear') {
      setHistory([])
      setNextId(id)
      return
    }

    const response = COMMANDS[trimmed] ?? [`command not found: ${trimmed}`, '']
    response.forEach(line => { newLines.push({ id: id++, type: 'output', text: line }) })

    setHistory(prev => [...prev, ...newLines].slice(-24))
    setNextId(id)
  }, [nextId])

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      run(input)
      setInput('')
    }
  }

  return (
    <div
      style={{ margin: '0 32px', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}
      onClick={() => inputRef.current?.focus()}
    >
      {/* Output area */}
      <div style={{ padding: '14px 20px 4px', minHeight: 88, maxHeight: 140, overflowY: 'auto' }}>
        {history.map(line => (
          <div key={line.id} style={{ fontFamily: MONO, fontSize: 11, lineHeight: '20px', color: line.type === 'input' ? C.accent : C.textDim, opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease' }}>
            {line.text || '\u00A0'}
          </div>
        ))}
      </div>
      {/* Input row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 20px 14px', gap: 10, opacity: mounted ? 1 : 0, transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 1200ms' }}>
        <span style={{ fontFamily: MONO, fontSize: 12, color: C.textDim }}>$</span>
        <div style={{ flex: 1, position: 'relative', borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="type a command..."
            style={{
              width: '100%', background: 'transparent', border: 'none', outline: 'none',
              fontFamily: MONO, fontSize: 12, color: C.accent, caretColor: C.accent,
            }}
          />
          {!input && (
            <span style={{ position: 'absolute', right: 0, top: 0, display: 'inline-block', width: 1, height: 14, background: C.accent, verticalAlign: 'middle', animation: 'dp-blink 1.2s steps(1) infinite', opacity: 0.6 }} />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Docs section card ─────────────────────────────────────────────────────────

function DocCard({ title, desc, tag, href, visible = true, delay = 0 }: { title: string; desc: string; tag: string; href: string; visible?: boolean; delay?: number }) {
  const [hovered, setHovered] = useState(false)
  return (
    <Link
      to={href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block', padding: '24px', background: hovered ? C.surfaceHover : C.surface,
        border: `1px solid ${hovered ? C.accent + '20' : C.border}`,
        textDecoration: 'none', cursor: 'pointer',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, background 0.2s ease, border-color 0.2s ease`,
      }}
    >
      <div style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>{tag}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 400, color: hovered ? C.white : C.accent, marginBottom: 8, transition: 'color 0.2s' }}>{title}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textDim, lineHeight: 1.5 }}>{desc}</div>
    </Link>
  )
}

// ── Page component ────────────────────────────────────────────────────────────

export default function DocsPage() {
  const [mounted, setMounted] = useState(false)
  const [headerVis, setHeaderVis] = useState(false)
  const [metricsVis, setMetricsVis] = useState(false)
  const [activeStage, setActiveStage] = useState(2)
  const [cycle, setCycle] = useState(48291)
  const [entropy, setEntropy] = useState(0.0023)
  const [log, setLog] = useState(() =>
    LOG_POOL.slice(0, 5).map((e, i) => ({ ...e, time: `00:0${i}:${22 + i * 4}` }))
  )
  const [cardsVis, setCardsVis] = useState(false)

  useEffect(() => {
    setMounted(true)
    const t1 = setTimeout(() => setHeaderVis(true), 200)
    const t2 = setTimeout(() => setMetricsVis(true), 500)
    const t3 = setTimeout(() => setCardsVis(true), 1600)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStage(prev => (prev + 1) % PIPELINE.length)
      setCycle(prev => prev + 1)
      setEntropy(prev => Math.max(0.0001, prev + (Math.random() - 0.5) * 0.0002))
      const pick = LOG_POOL[Math.floor(Math.random() * LOG_POOL.length)]
      setLog(prev => [{ ...pick, time: nowTime() }, ...prev].slice(0, 5))
    }, 5000)
    return () => clearInterval(id)
  }, [])

  const starsVis = useStagger(STARS.length, 70, 800)
  const pipeVis  = useStagger(PIPELINE.length, 100, 400)
  const actVis   = useStagger(log.length, 60, 1000)
  const cardVis  = useStagger(8, 60, 1600)

  const metrics = [
    { label: 'Modules', value: '692',             unit: 'loaded' },
    { label: 'Uptime',  value: '99.99',           unit: '%'      },
    { label: 'Entropy', value: entropy.toFixed(4)                },
    { label: 'Cycle',   value: `#${cycle.toLocaleString()}`      },
  ]

  const docs = [
    { title: 'Quick Start',        desc: '5-minute guide to your first consciousness API call',                    tag: 'Getting Started', href: '/docs/quickstart'   },
    { title: 'Authentication',     desc: 'Set up ΛiD OAuth, passkeys, and API key auth',                          tag: 'Security',        href: '/docs/auth'          },
    { title: 'Core Concepts',      desc: 'Understand consciousness, MATRIZ pipeline, and Constellation',           tag: 'Concepts',        href: '/docs/concepts'      },
    { title: 'Consciousness API',  desc: 'POST /v1/consciousness/query — context-aware reasoning',                 tag: 'API Reference',   href: '/api-reference'      },
    { title: 'Memory & Context',   desc: 'Persistent memory folds, recall, and context preservation',             tag: 'API Reference',   href: '/api-reference'      },
    { title: 'Guardian & Ethics',  desc: 'Drift detection, constitutional alignment, and compliance endpoints',   tag: 'Governance',      href: '/api-reference'      },
    { title: 'Python SDK',         desc: 'pip install lukhas — idiomatic Python client with async support',       tag: 'SDK',             href: '/docs/sdk/python'    },
    { title: 'JavaScript SDK',     desc: 'npm install @lukhas/sdk — TypeScript-first with full type coverage',    tag: 'SDK',             href: '/docs/sdk/javascript' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: DISPLAY }}>

      {/* ── Nav ── */}
      <Header className="fixed top-0 left-0 right-0 z-50 bg-consciousness-deep/80 backdrop-blur-md border-b border-white/5">
        <HeaderLogo href="/">
          <span style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 300, letterSpacing: '0.15em', color: C.accent }}>
            LUKH&#923;S<span style={{ color: C.textDim }}>.DEV</span>
          </span>
        </HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
          <HeaderNavLink href="/api-reference">API</HeaderNavLink>
          <HeaderNavLink href="/constellation">Constellation</HeaderNavLink>
          <HeaderNavLink href="/playground">Playground</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <a href="https://lukhas.id/login" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" style={{ fontFamily: MONO, fontSize: 11, color: C.textDim }}>Sign In</Button>
          </a>
        </HeaderActions>
      </Header>

      {/* ── Page title ── */}
      <section style={{ paddingTop: 96, paddingBottom: 32, paddingLeft: 32, paddingRight: 32 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ opacity: headerVis ? 1 : 0, transform: headerVis ? 'translateY(0)' : 'translateY(-8px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Developer Docs &amp; API Portal</div>
            <h1 style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 300, color: C.white, letterSpacing: '0.04em', margin: 0 }}>
              LUKH&#923;S Documentation
            </h1>
            <p style={{ fontFamily: DISPLAY, fontSize: 15, color: C.textDim, marginTop: 10, maxWidth: 560 }}>
              Build with consciousness-native APIs. Explore the Constellation Framework, MATRIZ pipeline, and sovereign identity layer.
            </p>
          </div>
        </div>
      </section>

      {/* ── CLI / Constellation dashboard hero ── */}
      <section style={{ padding: '0 32px', paddingBottom: 48 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ background: C.bg, color: C.text, fontFamily: DISPLAY, position: 'relative', overflow: 'hidden', border: `1px solid ${C.border}` }}>

            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '32px 32px 0', opacity: headerVis ? 1 : 0, transform: headerVis ? 'translateY(0)' : 'translateY(-10px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <LambdaMark />
                <div>
                  <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 300, color: C.white, letterSpacing: '0.08em' }}>LUKH&#923;S</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', marginTop: 2 }}>CONSCIOUSNESS BY DESIGN</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>MODE</div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: C.textDim, marginTop: 2 }}>live</div>
                </div>
                <div style={{ width: 1, height: 28, background: C.border }} />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>TIER</div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: C.accent, marginTop: 2 }}>SOVEREIGN</div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: C.border, margin: '24px 32px', opacity: headerVis ? 1 : 0, transform: headerVis ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 400ms' }} />

            {/* Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, margin: '0 32px 24px', background: C.border, border: `1px solid ${C.border}` }}>
              {metrics.map((m, i) => (
                <div key={m.label} style={{ background: C.surface, padding: '18px 20px' }}>
                  <MetricCell {...m} visible={metricsVis} delay={i * 80} />
                </div>
              ))}
            </div>

            {/* Main grid: constellation table + pipeline/activity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 1, margin: '0 32px', background: C.border, border: `1px solid ${C.border}` }}>

              {/* Left: constellation table */}
              <div style={{ background: C.surface }}>
                <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <SectionLabel visible={mounted} delay={600}>Constellation</SectionLabel>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px', alignItems: 'center', padding: '8px 20px', borderBottom: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease 700ms' }}>
                  {['Code', 'Module', 'Status', 'Health', ''].map(h => (
                    <span key={h} style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{h}</span>
                  ))}
                </div>
                {STARS.map((s, i) => (
                  <StarRowItem key={s.code} {...s} idx={i} visible={starsVis[i]} />
                ))}
              </div>

              {/* Right: pipeline + activity */}
              <div style={{ background: C.surface, display: 'flex', flexDirection: 'column' }}>
                {/* Pipeline */}
                <div style={{ padding: '16px 20px 20px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <SectionLabel visible={mounted} delay={300}>MATRIZ Pipeline</SectionLabel>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16, position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 18, left: 18, right: 18, height: 1, background: C.border, zIndex: 0 }} />
                    <div style={{ position: 'absolute', top: 18, left: 18, width: mounted ? `${(activeStage / (PIPELINE.length - 1)) * 100}%` : '0%', height: 1, background: C.textDim, zIndex: 0, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
                    {PIPELINE.map((s, i) => (
                      <div key={s.letter} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: pipeVis[i] ? 1 : 0, transform: pipeVis[i] ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.95)', transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${400 + i * 100}ms` }}>
                        <div style={{ width: 36, height: 36, border: `1.5px solid ${i === activeStage ? C.accent : i < activeStage ? C.textDim : C.border}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 13, fontWeight: 500, color: i === activeStage ? C.white : i < activeStage ? C.textDim : C.textGhost, background: i === activeStage ? `${C.accent}0A` : 'transparent', transition: 'all 0.6s ease', position: 'relative' }}>
                          {s.letter}
                          {i === activeStage && <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: `1px solid ${C.accent}15`, animation: 'dp-pulse 3s ease-in-out infinite' }} />}
                        </div>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: i === activeStage ? C.text : C.textGhost, letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'color 0.3s' }}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, padding: '10px 0 0', borderTop: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1000ms' }}>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost }}>CYCLE</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim }}>#{cycle.toLocaleString()}</span>
                  </div>
                </div>

                {/* Activity log */}
                <div style={{ flex: 1, padding: '16px 20px' }}>
                  <SectionLabel visible={mounted} delay={800}>Activity</SectionLabel>
                  <div style={{ marginTop: 8 }}>
                    {log.map((a, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '68px 1fr', gap: 12, padding: '7px 0', opacity: actVis[i] ? 1 : 0, transform: actVis[i] ? 'translateY(0)' : 'translateY(4px)', transition: `all 0.4s cubic-bezier(0.16,1,0.3,1) ${600 + i * 60}ms` }}>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: C.textGhost }}>{a.time}</span>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: a.type === 'success' ? C.text : C.textDim }}>{a.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive terminal */}
            <TerminalPanel mounted={mounted} />

            {/* Footer strip */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 32px 24px', opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1400ms' }}>
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>LUKH&#923;S v4.2.1 — 8-STAR CONSTELLATION FRAMEWORK</span>
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>TRY: help / status / list modules</span>
            </div>

            <style>{`
              @keyframes dp-breathe { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.15); opacity: 1; } }
              @keyframes dp-pulse   { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.3); opacity: 0; } }
              @keyframes dp-blink   { 0%, 100% { opacity: 0.6; } 50% { opacity: 0; } }
            `}</style>
          </div>
        </div>
      </section>

      {/* ── Documentation cards ── */}
      <section style={{ padding: '0 32px 64px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          <div style={{ opacity: cardsVis ? 1 : 0, transform: cardsVis ? 'translateY(0)' : 'translateY(8px)', transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1)', marginBottom: 24 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>Documentation</div>
            <div style={{ height: 1, background: C.border }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: C.border, border: `1px solid ${C.border}` }}>
            {docs.map((d, i) => (
              <DocCard key={d.title} {...d} visible={cardVis[i]} delay={i * 60} />
            ))}
          </div>

          {/* Quick-install strip */}
          <div style={{ marginTop: 1, border: `1px solid ${C.border}`, borderTop: 'none', background: C.surface, padding: '20px 24px', opacity: cardsVis ? 1 : 0, transition: 'opacity 0.5s ease 700ms' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Install</span>
              {[
                { label: 'Python', cmd: 'pip install lukhas' },
                { label: 'Node',   cmd: 'npm install @lukhas/sdk' },
                { label: 'Go',     cmd: 'go get lukhas.io/sdk' },
              ].map(({ label, cmd }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost }}>{label}</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: C.textDim, background: C.bg, padding: '4px 10px', border: `1px solid ${C.border}` }}>{cmd}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer activeDomain="lukhas.dev">
        <div>
          <h3 style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Developer Resources</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['Documentation', '/docs'], ['API Reference', '/api-reference'], ['Playground', '/playground'], ['Constellation CLI', '/constellation']].map(([label, href]) => (
              <li key={label}><Link to={href} style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textDim, textDecoration: 'none' }}>{label}</Link></li>
            ))}
          </ul>
        </div>
      </Footer>
    </div>
  )
}
